import { ValidationError as ValidationErrorFromYup } from 'yup'
import { ApiResponsePlaceholderType } from '../types/ApiResponseTypes'
import { HANDLED_APPLICATION_ERROR } from './constants/error-codes'

export enum Severity {
    Error = 'Error',
    Warning = 'Warning',
    Info = 'Info',
}

export interface ValidationErrorFromGateway {
    propertyName?: string | null
    errorCode?: string | null
    errorMessage?: string | null
    data?: any
    severity?: Severity
}

export interface HandledApplicationErrorDetails<TApiResponse = ApiResponsePlaceholderType> {
    formId: string
    validationErrors?: (ValidationErrorFromYup | ValidationErrorFromGateway)[]
    apiResponse?: TApiResponse
    cause?: Error
}

/**
 * HandledApplicationErrors are meant to be thrown and catched in the client. The client should catch every HandledApplicationError and do something meaningful with it.
 *
 * For example, when a formId is provided, the client can use the validationErrors to set the errors on the form fields.
 *
 * @see defaultDissectedErrorHandler in default error handling of dissectResponse, which transforms DissectedErrors into HandledApplicationErrors when a formId is provided.
 * @see useAction hook, which catches HandledApplicationErrors.
 * @see useValidation hook, which does something with HandledApplicationErrors.
 */
export class HandledApplicationError<TApiResponse = ApiResponsePlaceholderType> extends Error {
    formId: string
    apiResponse?: TApiResponse
    cause?: Error
    code: string
    private _validationErrors?: ValidationErrorFromYup[] | undefined
    constructor(message: string, details: HandledApplicationErrorDetails<TApiResponse>, ...params: ErrorOptions[]) {
        const errorOptions = params?.shift() || {}
        if (!!details.cause) errorOptions.cause = details.cause
        super(message, errorOptions)
        this.formId = details.formId
        this.apiResponse = details.apiResponse
        this.code = HANDLED_APPLICATION_ERROR
        this.message = message
        if (
            details.apiResponse &&
            typeof details.apiResponse === 'object' &&
            'validationErrors' in details.apiResponse
        ) {
            this.validationErrors = details.apiResponse?.validationErrors as unknown as ValidationErrorFromGateway[]
        }

        Object.setPrototypeOf(this, HandledApplicationError.prototype)
    }

    // getter for validationErrors array. It just returns the value. Later we will write a setter
    get validationErrors(): ValidationErrorFromYup[] | undefined {
        return this._validationErrors
    }

    set validationErrors(errors: (ValidationErrorFromGateway | ValidationErrorFromYup)[] | undefined) {
        this._validationErrors = errors?.map((error) => this.convertGatewayErrorToYupError(error))
    }

    private convertGatewayErrorToYupError(
        error: ValidationErrorFromGateway | ValidationErrorFromYup,
    ): ValidationErrorFromYup {
        if (error instanceof ValidationErrorFromYup) return error

        const yupError: ValidationErrorFromYup = new ValidationErrorFromYup(
            error.errorCode ?? 'no error message',
            error.data,
            error.propertyName ?? undefined,
        )
        return yupError
    }
}
